import { useTranslation } from 'react-i18next'
import { useFormik } from 'formik'

import { formatRTKQueryError, handleApiError } from '@entities/apiHandler'
import {
  SubscriptionFormValues,
  useCreateSubscriptionMutation,
} from '@shared/api/rtkQuery'
import { useAppDispatch } from '@shared/lib/hooks'
import { trackEvent } from '@shared/lib/utils/amplitude'
import { openToast } from '@shared/model/slices'
import { ConfirmDialog } from '@shared/ui/dialogs'

import {
  MUST_SUBSCRIPTION_COLUMNS,
  SUBSCRIPTION_ERR_CODE,
  SUBSCRIPTION_MAX_COLUMN,
  subscriptionSchema,
} from '../_shared'
import { Columns, Endpoint, Name } from './SubscriptionForm'

type Props = {
  isOpen: boolean
  onClose: () => void
}

const CreateSubscription = ({ isOpen, onClose }: Props) => {
  const { t } = useTranslation(['common', 'contentTemplate'])
  const dispatch = useAppDispatch()
  const [createSubscription, { isLoading: isCreating }] =
    useCreateSubscriptionMutation()

  const { values, touched, errors, setFieldValue, setTouched, handleSubmit } =
    useFormik<SubscriptionFormValues>({
      initialValues: {
        name: '',
        columns: MUST_SUBSCRIPTION_COLUMNS,
        url: '',
      },
      validationSchema: subscriptionSchema,
      onSubmit: async values => {
        try {
          const { id } = await createSubscription(values).unwrap()

          trackEvent('MarketingAutomationTemplateConfigured', {
            templateId: id,
            templateName: values.name,
            templateType: 'subscription',
          })

          onClose()
        } catch (error) {
          const typedError = formatRTKQueryError(error)

          if (typedError && typedError.data) {
            if (
              typedError.data.errorCode ===
              SUBSCRIPTION_ERR_CODE.COLUMN_NUMBER_EXCEEDS_LIMIT
            ) {
              dispatch(
                openToast({
                  status: 'error',
                  message: t(
                    'contentTemplate:subscription.error.column_number_exceeds_limit',
                    {
                      num: SUBSCRIPTION_MAX_COLUMN,
                    }
                  ),
                })
              )
              return
            }

            if (
              typedError.data.errorCode === SUBSCRIPTION_ERR_CODE.DUPLICATE_NAME
            ) {
              dispatch(
                openToast({
                  status: 'error',
                  message: t(
                    'contentTemplate:subscription.error.duplicate_name'
                  ),
                })
              )
              return
            }
          }

          dispatch(handleApiError(typedError))
        }
      },
    })

  return (
    <ConfirmDialog
      isOpen={isOpen}
      isLoading={isCreating}
      modalTitle={t('contentTemplate:subscription.dialog.common.modal_title')}
      onClose={onClose}
      onConfirm={handleSubmit}
      contentOverflowY="visible"
      maxWidth="md"
    >
      <Name
        value={values.name}
        onChange={value => {
          setTouched({ name: true })
          setFieldValue('name', value)
        }}
        isDisabled={isCreating}
        isError={Boolean(errors.name && touched.name)}
      />
      <Columns
        values={values.columns}
        onChange={value => {
          setTouched({ columns: true })
          setFieldValue('columns', value)
        }}
        isError={Boolean(errors.columns && touched.columns)}
        isDisabled={isCreating}
      />
      <Endpoint
        value={values.url}
        onChange={value => {
          setTouched({ url: true })
          setFieldValue('url', value)
        }}
        isDisabled={isCreating}
        isError={Boolean(errors.url && touched.url)}
        isValidator
      />
    </ConfirmDialog>
  )
}

export default CreateSubscription
